import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Grid,
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import { AuthService } from '../../../services/api/authService';
import { useAuth } from '../../../contexts/AuthContext';

interface ChallengeData {
  session: string;
  challengeParameters: any;
  username: string;
  password: string;
}

const NewPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = useAuth();

  // Extract challenge data passed via navigation state
  const state = location.state as { challengeData: ChallengeData } | undefined;
  const challengeData = state?.challengeData;

  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewPassword(e.target.value);
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!challengeData) {
      setError('Missing challenge data.');
      return;
    }
    setLoading(true);
    try {
      const user = {
        ChallengeParameters: challengeData.challengeParameters,
        Session: challengeData.session
      };
      // Complete the new password challenge
      await AuthService.completeNewPassword(user, newPassword);
      // After setting new password, attempt sign-in with updated credentials
      await signIn({ username: challengeData.username, password: newPassword });
      navigate('/', { replace: true });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!challengeData) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Alert severity="error">Error: No challenge data provided.</Alert>
      </Box>
    );
  }

  return (
    <Grid
      container
      sx={{ height: '100vh', backgroundColor: '#f5f5f5' }}
      alignItems="center"
      justifyContent="center"
    >
      <Grid item xs={10} sm={8} md={4}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          <Typography variant="h5" textAlign="center" sx={{ mb: 2 }}>
            Set a New Password
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="New Password"
              type="password"
              variant="outlined"
              fullWidth
              required
              value={newPassword}
              onChange={handleNewPasswordChange}
              autoComplete="new-password"
            />

            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ mt: 1 }}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Setting new password...
                </>
              ) : (
                'Set New Password'
              )}
            </Button>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default NewPasswordPage;
